import { Building2, CreditCard, FileText, MapPin, Phone } from "lucide-react";
import React, { useEffect, useState } from "react";
import { billingApi } from "../lib/api";
import { BillingDetails, CreateBillingDetailsRequest } from "../types/billing";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { useToast } from "./ui/use-toast";

interface BillingDetailsFormProps {
  onSuccess?: (billingDetails: BillingDetails) => void;
  onCheckout?: (checkoutUrl: string) => void;
}

const BillingDetailsForm: React.FC<BillingDetailsFormProps> = ({
  onSuccess,
  onCheckout,
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [existingBilling, setExistingBilling] = useState<BillingDetails | null>(
    null
  );
  const [amountToCharge, setAmountToCharge] = useState(0);
  const [formData, setFormData] = useState<CreateBillingDetailsRequest>({
    plan: "PRO",
    country: "",
    state: "",
    city: "",
    postalCode: "",
    phoneNumber: "",
    hasTaxNumber: false,
    companyName: "",
    taxNumber: "",
    taxType: "",
  });

  // Load existing billing details on component mount
  useEffect(() => {
    loadBillingDetails();
    getBillingAmount();
  }, []);

  const getBillingAmount = async () => {
    try {
      const response = await billingApi.getBillAmount();
      console.log("response", response);
      if (response.success && response.data?.amountToCharge) {
        setAmountToCharge(response.data.amountToCharge);
      }
    } catch (error) {
      console.error("Error loading billing details:", error);
    }
  };

  const loadBillingDetails = async () => {
    try {
      const response = await billingApi.getBillingDetails();
      if (response.success && response.data?.billingDetails) {
        const billing = response.data.billingDetails;
        setExistingBilling(billing);
        setFormData({
          plan: billing.plan,
          country: billing.country,
          state: billing.state,
          city: billing.city,
          postalCode: billing.postalCode,
          phoneNumber: billing.phoneNumber,
          hasTaxNumber: billing.hasTaxNumber,
          companyName: billing.companyName || "",
          taxNumber: billing.taxNumber || "",
          taxType: billing.taxType || "",
        });
      }
    } catch (error) {
      console.error("Error loading billing details:", error);
    }
  };

  const handleInputChange = (
    field: keyof CreateBillingDetailsRequest,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = (): boolean => {
    if (
      !formData.country ||
      !formData.state ||
      !formData.city ||
      !formData.postalCode ||
      !formData.phoneNumber
    ) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required billing address fields.",
        status: "error",
      });
      return false;
    }

    if (
      formData.hasTaxNumber &&
      (!formData.companyName || !formData.taxNumber || !formData.taxType)
    ) {
      toast({
        title: "Validation Error",
        description:
          "Please fill in all tax information fields when tax number is enabled.",
        status: "error",
      });
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const response = await billingApi.upsertBillingDetails(formData);
      if (response.success && response.data) {
        toast({
          title: "Success",
          description:
            response.data.message || "Billing details saved successfully",
          status: "success",
        });
        setExistingBilling(response.data.billingDetails);
        onSuccess?.(response.data.billingDetails);
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to save billing details",
          status: "error",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save billing details",
        status: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const ensureRazorpayLoaded = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (typeof (window as any).Razorpay !== "undefined") {
        resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
      document.body.appendChild(script);
    });
  };

  const handleCheckout = async () => {
    if (!validateForm()) return;

    setIsCheckoutLoading(true);
    try {
      // Initiate checkout to create order (server enforces Rs 500)
      const response = await billingApi.checkout({
        billingDetails: formData,
        amount: amountToCharge,
        redirectUrl: window.location.origin + "/payment-success",
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || "Failed to initiate checkout");
      }

      const { orderId, amount, currency, key } = response.data;

      await ensureRazorpayLoaded();

      const options: any = {
        key,
        amount, // in paise
        currency,
        name: "QuickDB",
        description: `${formData.plan} Plan Subscription`,
        order_id: orderId,
        prefill: {
          name: existingBilling?.companyName || "",
          email: existingBilling?.email || "",
          contact: existingBilling?.phoneNumber || formData.phoneNumber || "",
        },
        notes: {
          plan: formData.plan,
        },
        handler: async (payment: any) => {
          try {
            const verifyRes = await billingApi.verifyPayment({
              razorpay_order_id: payment.razorpay_order_id,
              razorpay_payment_id: payment.razorpay_payment_id,
              razorpay_signature: payment.razorpay_signature,
            });

            if (verifyRes.success && verifyRes.data?.success) {
              toast({
                title: "Payment Successful",
                description: "Your subscription has been activated.",
                status: "success",
              });
              // Refresh billing details
              await loadBillingDetails();
              await getBillingAmount();
            } else {
              throw new Error(verifyRes.error || "Payment verification failed");
            }
          } catch (err: any) {
            toast({
              title: "Verification Error",
              description: err.message || "Failed to verify payment",
              status: "error",
            });
          }
        },
        modal: {
          ondismiss: () => {
            toast({
              title: "Payment Cancelled",
              description:
                "You closed the payment window before completing the payment.",
              status: "error",
            });
          },
        },
        theme: {
          color: "#16a34a",
        },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to initiate checkout",
        status: "error",
      });
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const getTaxTypeLabel = (country: string): string => {
    switch (country.toUpperCase()) {
      case "INDIA":
        return "GSTIN";
      case "UNITED KINGDOM":
        return "VAT";
      case "UNITED STATES":
        return "EIN";
      default:
        return "Tax Number";
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Plan Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Plan Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="plan">Plan</Label>
              <Select
                value={formData.plan}
                onValueChange={(value) => handleInputChange("plan", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PRO">Pro Plan</SelectItem>
                  <SelectItem value="ENTERPRISE">Enterprise Plan</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing Address */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Billing Address
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="country">Country *</Label>
              <Select
                value={formData.country}
                onValueChange={(value) => handleInputChange("country", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="India">India</SelectItem>
                  <SelectItem value="United States">United States</SelectItem>
                  <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                  <SelectItem value="Canada">Canada</SelectItem>
                  <SelectItem value="Australia">Australia</SelectItem>
                  <SelectItem value="Germany">Germany</SelectItem>
                  <SelectItem value="France">France</SelectItem>
                  <SelectItem value="Japan">Japan</SelectItem>
                  <SelectItem value="Singapore">Singapore</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="state">State/Province *</Label>
              <Input
                id="state"
                value={formData.state}
                onChange={(e) => handleInputChange("state", e.target.value)}
                placeholder="Enter state or province"
              />
            </div>

            <div>
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
                placeholder="Enter city"
              />
            </div>

            <div>
              <Label htmlFor="postalCode">Postal Code *</Label>
              <Input
                id="postalCode"
                value={formData.postalCode}
                onChange={(e) =>
                  handleInputChange("postalCode", e.target.value)
                }
                placeholder="Enter postal code"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="phoneNumber" className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone Number *
              </Label>
              <Input
                id="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) =>
                  handleInputChange("phoneNumber", e.target.value)
                }
                placeholder="Enter phone number"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tax Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Tax Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="hasTaxNumber"
                checked={formData.hasTaxNumber}
                onCheckedChange={(checked) =>
                  handleInputChange("hasTaxNumber", checked)
                }
              />
              <Label htmlFor="hasTaxNumber">
                I have a tax number (GSTIN, VAT, EIN, etc.)
              </Label>
            </div>

            {formData.hasTaxNumber && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="companyName"
                    className="flex items-center gap-2"
                  >
                    <Building2 className="w-4 h-4" />
                    Company Name *
                  </Label>
                  <Input
                    id="companyName"
                    value={formData.companyName}
                    onChange={(e) =>
                      handleInputChange("companyName", e.target.value)
                    }
                    placeholder="Enter company name"
                  />
                </div>

                <div>
                  <Label htmlFor="taxType">Tax Type *</Label>
                  <Select
                    value={formData.taxType}
                    onValueChange={(value) =>
                      handleInputChange("taxType", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tax type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GSTIN">GSTIN (India)</SelectItem>
                      <SelectItem value="VAT">VAT (UK/EU)</SelectItem>
                      <SelectItem value="EIN">EIN (US)</SelectItem>
                      <SelectItem value="ABN">ABN (Australia)</SelectItem>
                      <SelectItem value="GST">GST (Canada)</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="taxNumber">
                    {formData.taxType
                      ? getTaxTypeLabel(formData.country)
                      : "Tax Number"}{" "}
                    *
                  </Label>
                  <Input
                    id="taxNumber"
                    value={formData.taxNumber}
                    onChange={(e) =>
                      handleInputChange("taxNumber", e.target.value)
                    }
                    placeholder={`Enter ${
                      formData.taxType
                        ? getTaxTypeLabel(formData.country)
                        : "tax number"
                    }`}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="border rounded-xl p-4 shadow-sm ml-auto bg-white max-w-xs">
        <p className="text-gray-600 text-sm">Amount to Pay</p>
        <p className="text-2xl font-bold text-green-600">₹{amountToCharge}</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 justify-end">
        <Button variant="outline" onClick={handleSave} disabled={isLoading}>
          {isLoading ? "Saving..." : "Save Billing Details"}
        </Button>

        <Button
          onClick={handleCheckout}
          disabled={isCheckoutLoading}
          className="bg-green-600 hover:bg-green-700"
        >
          {isCheckoutLoading ? "Processing..." : "Proceed to Checkout"}
        </Button>
      </div>
    </div>
  );
};

export default BillingDetailsForm;
